import type React from "react";
import { Children } from "react";
import { Box } from "../Box/Box";
import type { ResponsiveSpace } from "../Box/useBoxStyles";
import { Divider, type DividerProps } from "../Divider/Divider";
import type { ReactNodeNoStrings } from "../private/ReactNodeNoStrings";
import {
  negativeMarginLeft,
  negativeMarginTop,
} from "../utils/negativeMarginCss";
import {
  normaliseResponsiveProp, // resolveResponsiveProp,
  type ResponsiveProp,
} from "../utils/responsiveProp";
import styles from "./Tiles.module.scss";

export interface TilesProps {
  children: ReactNodeNoStrings;
  space: ResponsiveSpace;
  columns: ResponsiveProp<1 | 2 | 3 | 4 | 5 | 6>;
  dividers?: boolean | DividerProps["color"];
}

export const Tiles = ({
  children,
  space = "none",
  columns = 1,
  dividers = false,
}: TilesProps): React.ReactNode => {
  const responsiveSpace = normaliseResponsiveProp(space);

  const [mobileColumns, tabletColumns, desktopColumns] =
    normaliseResponsiveProp(columns);

  const [lmClassName, lmCss] = negativeMarginLeft(space);
  const [tmClassName, tmCss] = negativeMarginTop(space);

  return (
    <Box className={tmClassName}>
      {lmCss}
      {tmCss}

      <Box display="flex" flexWrap="wrap" className={lmClassName}>
        {Children.map(children, (child, i) =>
          !child ? null : (
            <Box
              minWidth={0}
              // TODO:, not sure how to do this yet
              className={getResponsiveClasses("flex", columns)}
            >
              <Box
                height="full"
                // This needs to be a separate element to support IE11.
                paddingTop={responsiveSpace}
                paddingLeft={responsiveSpace}
              >
                {dividers && i > 0 ? (
                  <Box
                    paddingBottom={responsiveSpace}
                    display={[
                      mobileColumns === 1 ? "block" : "none",
                      tabletColumns === 1 ? "block" : "none",
                      desktopColumns === 1 ? "block" : "none",
                    ]}
                  >
                    {typeof dividers === "string" ? (
                      <Divider color={dividers} />
                    ) : (
                      <Divider />
                    )}
                  </Box>
                ) : null}
                {child}
              </Box>
            </Box>
          ),
        )}
      </Box>
    </Box>
  );
};

function getClasses(name: string, value?: string | number) {
  if (!value) return null;

  if (!styles[`${name}_${value}`]) {
    console.warn("no style found for", `${name}_${value}`);
  }

  return styles[`${name}_${value}`];
}

function getResponsiveClasses<Keys extends string | number>(
  name: string,
  value?: ResponsiveProp<Keys>,
) {
  if (!value) return null;

  if (typeof value === "string" || typeof value === "number") {
    return styles[`${name}_${value}`];
  }

  const [mobileValue, tabletValue, desktopValue] =
    normaliseResponsiveProp(value);

  return [
    getClasses(name, mobileValue),
    getClasses(`${name}_tablet`, tabletValue),
    getClasses(`${name}_desktop`, desktopValue),
  ]
    .filter(Boolean)
    .join(" ");
}
